import { useCallback, useState } from "react";

import useNonce from "@hooks/useNonce";
import debounce from "@utils/debounce";

import { OrderFields } from "../../../constants";
import loadPackageSizes from "../../../services/loadPackageSizes";
import type { TPackageSize } from "../../../types";
import useOrderFieldsLoading from "../../useOrderFieldsLoading";

type TUpdatePackageSizesReturn = {
  packageSizes: TPackageSize[];
  updatePackageSizes: (countryFrom?: string, countryTo?: string, deliveryMethod?: string) => void;
};

const debouncedPackageSizesLoading = debounce((func) => func(), 200);

const useUpdatePackageSizes = (initialPackageSizes: TPackageSize[]): TUpdatePackageSizesReturn => {
  const [packageSizes, setPackageSizes] = useState(initialPackageSizes);

  const { isFieldLoading, setFieldLoading } = useOrderFieldsLoading();

  const { updateNonce, getCurrentNonce } = useNonce();

  const isCountriesToLoading = isFieldLoading(OrderFields.DESTINATION_COUNTRY);
  const updatePackageSizes = useCallback(
    (countryFrom?: string, countryTo?: string, deliveryMethod?: string) => {
      const localNonce = updateNonce();

      if (!countryFrom || !countryTo) {
        setPackageSizes([]);
        setFieldLoading(OrderFields.PACKAGE_SIZE, false);
        return;
      }

      setFieldLoading(OrderFields.PACKAGE_SIZE, true);

      debouncedPackageSizesLoading(async () => {
        if (localNonce !== getCurrentNonce()) {
          return;
        }
        if (isCountriesToLoading) {
          return;
        }

        const newPackageSizes = await loadPackageSizes({ countryFrom, countryTo, deliveryMethod });
        setPackageSizes(newPackageSizes);

        setFieldLoading(OrderFields.PACKAGE_SIZE, false);
      });
    },
    [getCurrentNonce, isCountriesToLoading, setFieldLoading, updateNonce]
  );

  return { packageSizes, updatePackageSizes };
};
export default useUpdatePackageSizes;
